import React, { useState } from "react";
import {
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { apiClient } from "../../utils/apiClient";
import { useSnackbar } from "../../components/SnackbarProvider";
import {
  EMAIL_ACTIONS,
  getAllActions,
  TestSetSelectorDialog,
  WorkflowEntityDialog,
  type EmailActionType,
  type AddToTestSetParams,
  type CreateWorkflowEntityParams,
  type SingleEmailActionResponse,
} from "../../admin/email/emailActions";

interface EmailAdminActionsProps {
  emailId?: string;
  messageId?: string;
  inboxEmail?: string;
  sharedInbox?: string;
  children?: React.ReactNode;
}

/**
 * Admin actions for emails - dropdown with various admin operations
 * Uses the unified email action API for all operations
 */
export const EmailAdminActions: React.FC<EmailAdminActionsProps> = ({
  emailId,
  messageId,
  inboxEmail,
  sharedInbox,
  children,
}) => {
  const [selectedAction, setSelectedAction] = useState<EmailActionType | "">(
    emailId ? "copyTestFixture" : "storeEmailInS3"
  );
  const [isExecuting, setIsExecuting] = useState(false);
  const [storeEmailDialogOpen, setStoreEmailDialogOpen] = useState(false);
  const [storedEmailId, setStoredEmailId] = useState<string | null>(null);
  const [testSetDialogOpen, setTestSetDialogOpen] = useState(false);
  const [workflowEntityDialogOpen, setWorkflowEntityDialogOpen] = useState(false);
  const { showSnackbar } = useSnackbar();

  const handleActionChange = (event: any) => {
    const action = event.target.value;
    setSelectedAction(action);
  };

  const handleExecuteAction = async () => {
    if (!selectedAction) return;

    const action = selectedAction as EmailActionType;
    const actionDef = EMAIL_ACTIONS[action];

    if (actionDef.requiresDialog) {
      if (action === "addToTestSet") {
        setTestSetDialogOpen(true);
      } else if (action === "createWorkflowEntity") {
        setWorkflowEntityDialogOpen(true);
      }
      return;
    }

    // Build email reference
    const email = {
      emailId,
      messageId,
      inboxEmail,
      sharedInbox,
    };

    setIsExecuting(true);
    try {
      const response = await apiClient.post<SingleEmailActionResponse>(
        "/admin/email_action",
        {
          action,
          email,
        },
        { withCredentials: true }
      );

      if (response.success) {
        if (action === "storeEmailInS3" && response.emailId) {
          setStoredEmailId(response.emailId);
          setStoreEmailDialogOpen(true);
          showSnackbar("Email stored in S3 successfully!", 3000, "success");
        } else if (action === "copyTestFixture" && response.data?.fixture) {
          await navigator.clipboard.writeText(response.data.fixture);
          showSnackbar("Test email fixture copied to clipboard!", 3000, "info");
        } else {
          showSnackbar("Action completed successfully!", 3000, "success");
        }
      } else {
        showSnackbar(response.error || "Action failed", 4000, "error");
      }
    } catch (error: any) {
      console.error("Email action error:", error);
      const errorMessage =
        error?.response?.data?.error || error.message || "Action failed";
      showSnackbar(errorMessage, 4000, "error");
    } finally {
      setIsExecuting(false);
      setSelectedAction("");
    }
  };

  const handleTestSetConfirm = async (params: AddToTestSetParams) => {
    const email = {
      emailId,
      messageId,
      inboxEmail,
      sharedInbox,
    };

    setIsExecuting(true);
    try {
      const response = await apiClient.post<SingleEmailActionResponse>(
        "/admin/email_action",
        {
          action: "addToTestSet",
          email,
          params,
        },
        { withCredentials: true }
      );

      if (response.success) {
        showSnackbar("Email added to test set!", 3000, "success");
      } else {
        showSnackbar(response.error || "Action failed", 4000, "error");
      }
    } catch (error: any) {
      console.error("Email action error:", error);
      const errorMessage =
        error?.response?.data?.error || error.message || "Action failed";
      showSnackbar(errorMessage, 4000, "error");
    } finally {
      setIsExecuting(false);
      setTestSetDialogOpen(false);
      setSelectedAction("");
    }
  };

  const handleWorkflowEntityConfirm = async (params: CreateWorkflowEntityParams) => {
    const email = {
      emailId,
      messageId,
      inboxEmail,
      sharedInbox,
    };

    setIsExecuting(true);
    try {
      const response = await apiClient.post<SingleEmailActionResponse>(
        "/admin/email_action",
        {
          action: "createWorkflowEntity",
          email,
          params,
        },
        { withCredentials: true }
      );

      if (response.success) {
        const alreadyExists = response.data?.alreadyInSet;
        if (alreadyExists) {
          showSnackbar("Workflow entity already exists for this email", 3000, "info");
        } else {
          showSnackbar("Workflow entity created successfully!", 3000, "success");
        }
      } else {
        showSnackbar(response.error || "Action failed", 4000, "error");
      }
    } catch (error: any) {
      console.error("Email action error:", error);
      const errorMessage =
        error?.response?.data?.error || error.message || "Action failed";
      showSnackbar(errorMessage, 4000, "error");
    } finally {
      setIsExecuting(false);
      setWorkflowEntityDialogOpen(false);
      setSelectedAction("");
    }
  };

  const copyEmailIdToClipboard = async () => {
    if (storedEmailId) {
      try {
        await navigator.clipboard.writeText(storedEmailId);
        showSnackbar("Email ID copied to clipboard!", 2000, "info");
      } catch (err) {
        console.error("Failed to copy:", err);
        showSnackbar("Failed to copy to clipboard", 2000, "error");
      }
    }
  };

  // Filter actions based on available data
  const availableActions = getAllActions().filter((action) => {
    switch (action.id) {
      case "copyTestFixture":
        return emailId || (messageId && inboxEmail);
      case "storeEmailInS3":
        return messageId && inboxEmail;
      case "addToTestSet":
        return emailId || (messageId && inboxEmail);
      case "createWorkflowEntity":
        return emailId || (messageId && inboxEmail);
      default:
        return false;
    }
  });

  return (
    <Box sx={{ mb: 2, display: "flex", gap: 1, alignItems: "center" }}>
      <FormControl size="small" sx={{ minWidth: 200 }} disabled={isExecuting}>
        <InputLabel id="admin-action-label">Admin Actions</InputLabel>
        <Select
          labelId="admin-action-label"
          id="admin-action-select"
          value={selectedAction}
          label="Admin Actions"
          onChange={handleActionChange}
        >
          {availableActions.map((action) => {
            const IconComponent = action.icon;
            return (
              <MenuItem key={action.id} value={action.id}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <IconComponent fontSize="small" />
                  <span>{action.label}</span>
                </Box>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      <Button
        variant="contained"
        size="small"
        onClick={handleExecuteAction}
        disabled={!selectedAction || isExecuting}
      >
        {isExecuting ? <CircularProgress size={20} /> : "Execute"}
      </Button>
      {children}

      {/* Dialog to show stored email ID */}
      <Dialog
        open={storeEmailDialogOpen}
        onClose={() => setStoreEmailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Email Stored Successfully
          <IconButton
            aria-label="close"
            onClick={() => setStoreEmailDialogOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
              Email ID:
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: "monospace",
                backgroundColor: "#f5f5f5",
                padding: "4px 8px",
                borderRadius: "4px",
                flexGrow: 1,
              }}
            >
              {storedEmailId}
            </Typography>
            <IconButton
              size="small"
              onClick={copyEmailIdToClipboard}
              title="Copy Email ID"
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary">
            The email has been successfully stored in S3. Use this email ID to
            access the stored email.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStoreEmailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Test Set Selector Dialog */}
      <TestSetSelectorDialog
        open={testSetDialogOpen}
        onClose={() => setTestSetDialogOpen(false)}
        onConfirm={handleTestSetConfirm}
        isLoading={isExecuting}
        selectedCount={1}
      />

      {/* Workflow Entity Dialog */}
      <WorkflowEntityDialog
        open={workflowEntityDialogOpen}
        onClose={() => setWorkflowEntityDialogOpen(false)}
        onConfirm={handleWorkflowEntityConfirm}
        isLoading={isExecuting}
      />
    </Box>
  );
};
